import { useState, useEffect, useCallback } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Toggle from '@cloudscape-design/components/toggle';
import Alert from '@cloudscape-design/components/alert';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { getLastSyncTime, getQueueStatus } from '../../api/tickets';
import { triggerSyncWithCooldown, isSyncInProgress } from '../../utils/sync';
import { CoverageIndicator } from '../CoverageIndicator';

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleString(); } catch { return dateStr; }
}

export function DataSyncSettings() {
  const [lastSynced, setLastSynced] = useState('');
  const [queueDepth, setQueueDepth] = useState(0);
  const [dlqDepth, setDlqDepth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fullSync, setFullSync] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ts, qs] = await Promise.all([getLastSyncTime(), getQueueStatus()]);
      setLastSynced(ts || '');
      setQueueDepth(qs.queue.messagesAvailable + qs.queue.messagesInFlight);
      setDlqDepth(qs.dlq.messagesAvailable);
    } catch (e: any) {
      setMessage({ type: 'error', text: `Failed to load sync status: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const tick = () => setInProgress(isSyncInProgress());
    tick();
    const interval = setInterval(tick, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSync = async () => {
    if (isSyncInProgress()) {
      setMessage({ type: 'warning', text: 'A sync is already in progress. Please wait a few minutes before triggering another.' });
      return;
    }
    setSyncing(true);
    setMessage(null);
    try {
      await triggerSyncWithCooldown(fullSync);
      setInProgress(true);
      setMessage({ type: 'success', text: 'Sync started. Tickets will update in a few minutes.' });
    } catch (e: any) {
      setMessage({ type: 'error', text: `Failed to trigger sync: ${e.message}` });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Container
      header={
        <Header variant="h2" description="Fetch the latest tickets from Tickety using the filters and AI config above.">
          Data Sync
        </Header>
      }
    >
      <SpaceBetween size="m">
        {message && (
          <Alert type={message.type} dismissible onDismiss={() => setMessage(null)}>{message.text}</Alert>
        )}

        <SpaceBetween size="xs">
          <StatusIndicator type="info">Last synced: {loading ? 'Loading…' : (lastSynced ? formatDate(lastSynced) : '—')}</StatusIndicator>
          {queueDepth > 0 && (
            <StatusIndicator type="in-progress">Queue: {queueDepth} ticket{queueDepth > 1 ? 's' : ''} pending summarization</StatusIndicator>
          )}
          {dlqDepth > 0 && (
            <StatusIndicator type="warning">{dlqDepth} ticket{dlqDepth > 1 ? 's' : ''} failed summarization (DLQ)</StatusIndicator>
          )}
          <CoverageIndicator />
        </SpaceBetween>

        <Box variant="small" color="text-body-secondary">
          An incremental sync fetches tickets updated recently. A full sync re-fetches everything matching the filters (slower).
        </Box>

        <SpaceBetween direction="horizontal" size="xs">
          <Toggle checked={fullSync} onChange={({ detail }) => setFullSync(detail.checked)}>Full sync</Toggle>
          <Button
            variant="primary"
            iconName="refresh"
            onClick={handleSync}
            loading={syncing}
            disabled={inProgress && !syncing}
          >
            {inProgress ? 'Sync in progress…' : 'Sync now'}
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
}
